import React, { useRef, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../lib/utils';
import { Button } from './ui/button';
import { Upload, Loader2, Trash2, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

// Reusable photo/logo uploader used across listing types (supervisor/internship/practice/employer).
// Uploads via POST /api/featured/upload-image and returns a data URL through onChange.
export const PhotoLogoUploader = ({ value, onChange, label = 'Professional Photo / Logo', testid = 'photo-logo-uploader' }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Image must be under 2MB'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API_URL}/featured/upload-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(r.data.image_url);
      toast.success('Image uploaded');
    } catch { toast.error('Upload failed'); } finally { setBusy(false); if (inputRef.current) inputRef.current.value = ''; }
  };

  return (
    <div data-testid={testid}>
      {label && <p className="text-sm font-semibold text-gray-700 mb-2">{label}</p>}
      <div className="flex items-center gap-4">
        <div className="w-24 h-24 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center overflow-hidden shrink-0">
          {value ? <img src={value} alt="preview" className="w-full h-full object-cover" data-testid="photo-preview" /> : <ImageIcon className="w-7 h-7 text-gray-300" />}
        </div>
        <div className="flex flex-col gap-2">
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="photo-input" />
          <Button type="button" variant="outline" size="sm" onClick={pick} disabled={busy} data-testid="photo-upload-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} {value ? 'Change' : 'Upload'}
          </Button>
          {value && (
            <Button type="button" variant="ghost" size="sm" className="text-red-600" onClick={() => onChange('')} data-testid="photo-remove-btn">
              <Trash2 className="w-4 h-4 mr-1" /> Remove
            </Button>
          )}
        </div>
      </div>
      <p className="text-xs text-gray-400 mt-1">JPG or PNG, up to 2MB.</p>
    </div>
  );
};

export default PhotoLogoUploader;
